import { useMutation } from "@tanstack/react-query";
import { format as formatDate } from "date-fns";
import FileSaver from "file-saver";
import type { TransactionFilterParams } from "Components/Transactions/utils/transactionFilterUtils";
import {
  getApiUrl,
  getQueryParamsString,
  getTransactionsExportPath,
} from "Infrastructure/Api/Api";
import { JWT_KEY } from "Utils/AuthUtils";
import { getLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";

export type ExportFormat = "Csv" | "Pdf" | "Xlsx" | "Email";
type SupportedFileType = "csv" | "pdf" | "xlsx";

const FILE_TYPE_TO_FILE_EXTENSION = {
  Csv: "csv",
  Pdf: "pdf",
  Xlsx: "xlsx",
} as const satisfies Record<Exclude<ExportFormat, "Email">, SupportedFileType>;

type ExportParams = {
  format: ExportFormat;
  filters: TransactionFilterParams;
  email?: string;
};

type RequestConfig = {
  url: string;
  method: "GET" | "POST";
  body?: string;
  headers: Record<string, string>;
};

const getRequestConfig = (
  format: ExportFormat,
  filters: TransactionFilterParams,
  email?: string,
): RequestConfig => {
  const token = getLocalStorageItem(JWT_KEY);
  if (!token) {
    throw new Error("Authentication required for export");
  }

  const apiUrl = getApiUrl();
  const endpointPath = getTransactionsExportPath();
  const headers = { Authorization: token };
  const queryParams = getQueryParamsString({
    Status: filters.status,
    DateFrom: filters.dateFrom,
    DateTo: filters.dateTo,
    PayPointPublicID: filters.payPointPublicID,
    EmployeePublicID: filters.employeePublicID,
    Direction: filters.direction,
    IsRefundOrCancellation: filters.isRefundOrCancellation,
  });

  if (format === "Email") {
    return {
      url: `${apiUrl}${endpointPath}`,
      method: "POST",
      body: JSON.stringify({ ...filters, format, email }),
      headers: { ...headers, "Content-Type": "application/json" },
    };
  }

  // PDF, XLSX, and CSV use unified export endpoint with format parameter
  const formatQuery = queryParams
    ? `${queryParams}&format=${format}`
    : `?format=${format}`;

  return {
    url: `${apiUrl}${endpointPath}${formatQuery}`,
    method: "GET",
    headers,
  };
};

const fetchTransactionsExport = async (
  filters: TransactionFilterParams,
  exportFormat: ExportFormat,
  email?: string,
) => {
  const { url, method, body, headers } = getRequestConfig(
    exportFormat,
    filters,
    email,
  );

  const response = await fetch(url, {
    method,
    mode: "cors",
    headers,
    ...(body && { body }),
  });

  if (!response.ok) {
    throw new Error(`Export failed: ${response.status}`);
  }

  return response;
};

export const useExportTransactionsMutation = () =>
  useMutation({
    mutationFn: async ({ format, filters, email }: ExportParams) => {
      const response = await fetchTransactionsExport(filters, format, email);

      if (format === "Email") {
        return { success: true, isEmail: true };
      }

      const blob = await response.blob();
      const fileExtension = FILE_TYPE_TO_FILE_EXTENSION[format];
      const fileName = `transactions_${formatDate(new Date(), "yyyy-MM-dd")}.${fileExtension}`;
      FileSaver.saveAs(blob, fileName);

      return { success: true, isEmail: false };
    },
  });
